import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatINR } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { MethodPicker } from "../QuickBillDialog";
import { Loader2, Receipt, IndianRupee } from "lucide-react";
import { toast } from "sonner";

/**
 * Collect payment for a completed consultation.
 *
 * Two paths, both independent of queue status:
 *   1. entry has no invoice  → create the invoice (optionally with the payment)
 *   2. entry already has one → append a payment to the existing invoice ledger
 *
 * Partial payments are first-class: the amount defaults to the balance due but can
 * be lowered, leaving the invoice `partial`.
 */
export default function CollectPaymentDialog({ open, onOpenChange, entry, defaultFee, onCompleted }) {
  const invoice = entry?.invoice || null;
  const balance = invoice ? Number(invoice.balance_due || 0) : Number(defaultFee || 500);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const idemKey = useMemo(
    () => `pay_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
    [open, entry?.appointment_id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (open) {
      setAmount(String(balance || ""));
      setMethod("cash");
      setReference("");
      setError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e?.preventDefault?.();
    const amt = parseFloat(amount);
    if (!(amt > 0)) { setError("Enter a valid amount"); return; }
    setSubmitting(true);
    setError("");
    try {
      let result;
      if (invoice?.invoice_id) {
        const { data } = await api.post(`/invoices/${invoice.invoice_id}/payments`, {
          method,
          amount: amt,
          transaction_reference: reference || null,
          client_payment_id: idemKey,
        });
        result = data;
      } else {
        const { data } = await api.post("/invoices", {
          patient_id: entry.patient_id,
          items: [{ description: entry.reason || "Consultation", quantity: 1, unit_price: Number(defaultFee || amt) }],
          appointment_id: entry.appointment_id,
          visit_id: entry.visit_id || null,
          payments: [{ method, amount: amt, ref: reference || null }],
        });
        result = data;
      }
      const settled = Number(result.balance_due || 0) <= 0;
      toast.success(
        settled
          ? `${formatINR(amt)} received · bill settled`
          : `${formatINR(amt)} received · ${formatINR(result.balance_due)} balance`
      );
      onOpenChange(false);
      onCompleted && onCompleted(result);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="collect-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-blue-600" /> Collect payment
          </DialogTitle>
          <DialogDescription>
            {entry.patient_name}
            {invoice ? (
              <>
                {" · "}
                <span className="font-medium">{formatINR(invoice.total)}</span> total ·{" "}
                {formatINR(invoice.paid_amount)} paid · {formatINR(invoice.balance_due)} due
              </>
            ) : (
              " · no invoice yet, one will be created"
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="collect-amount">Amount received</Label>
            <div className="relative mt-1.5">
              <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="collect-amount"
                type="number"
                step="1"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9 h-11 text-lg font-semibold"
                data-testid="collect-amount-input"
              />
            </div>
            {invoice && Number(amount) > 0 && Number(amount) < Number(invoice.balance_due) && (
              <Badge variant="outline" className="mt-2 bg-amber-50 border-amber-200 text-amber-700">
                Partial · {formatINR(Number(invoice.balance_due) - Number(amount))} will stay due
              </Badge>
            )}
          </div>

          <div>
            <Label>Payment method</Label>
            <div className="mt-1.5">
              <MethodPicker value={method} onChange={setMethod} testidPrefix="collect-method" />
            </div>
          </div>

          {(method === "upi" || method === "bank_transfer" || method === "card") && (
            <div>
              <Label htmlFor="collect-ref">
                {method === "upi" ? "UPI / UTR reference" : method === "card" ? "Card last 4 / auth code" : "Bank reference"}
                <span className="text-slate-400 font-normal"> (optional)</span>
              </Label>
              <Input
                id="collect-ref"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={method === "upi" ? "e.g. 402912345678" : ""}
                className="mt-1.5 h-10"
                data-testid="collect-reference-input"
              />
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="collect-payment-error">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="collect-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="collect-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Record payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
